import { Context } from "Context";
import { useCallback, useContext, useEffect, useState } from "react";
import appConfig from "config.json";
import {
	decodeJsonWebToken,
	focusChatModal,
	isCurrentPathPresent,
	mapString,
	triggerEventListener,
} from "utils";
import { sendAnalyticsEvent } from "utils/analyticsFramework";
import { dynamicallyLoadScript } from "utils/externalScripts";

const {
	roomsAvailabilityScript,
	brands,
	chatInviteModalId,
	chatWindowId,
	chatFloatingButtonId,
} = appConfig;

const chatScriptKey = "lpTag";
const chatButtonClass = "navigation-app-chat";
const chatEventListener = "navigationChatListener";
const maxIdentitiesInterval = 20;
let executingAuthenticationLogic = false;
let isMounted = false;
let addedAnalyticsEvents = false;
let observer = null;
let sendedInteractionEvent = false;

const Chat = () => {
	const {
		config: { brand },
		profileData: { isLoading, user },
		chat: { chatModal, analyticsEvent },
		setChat,
		ariaLiveRef,
	} = useContext(Context);

	const [chatScript, setChatScript] = useState(null);
	const [conversationId, setConversationId] = useState(null);

	const chatConfiguration = appConfig.brands[brand]?.chatConfiguration || {};

	const removeFocusModalListener = () => {
		document.removeEventListener("keydown", addFocusModalEvent);
	};

	const addFocusModalEvent = (event) => {
		focusChatModal(event, chatModal, removeFocusModalListener);
	};

	const removeHandleNodeListener = () => {
		observer?.disconnect();
	};

	const setChatScriptValue = useCallback(() => {
		if (window[chatScriptKey] && isMounted) {
			setChatScript(window[chatScriptKey]);
			document.removeEventListener(chatScriptKey, setChatScriptValue);
		}
	}, []);

	const publishChatEvent = (authenticated = false) => {
		document.dispatchEvent(
			new CustomEvent("navigationChat", {
				detail: {
					authenticated,
					status: "ready",
				},
			})
		);
	};

	const chatEventLogic = (event = {}) => {
		if (event.detail?.triggerProactive) {
			chatScript.sdes?.push({
				type: "error",
				error: {
					contextId: window.location.href,
					code: "no-resorts-availability",
				},
			});
		} else if (event.detail?.type) {
			chatScript.sdes?.push(event.detail);
		}
	};

	const sendEvent = (eventKey, payload = {}) => {
		if (appConfig.analyticsEvents[eventKey]) {
			sendAnalyticsEvent(
				brand,
				appConfig.analyticsEvents[eventKey],
				user?.swid,
				payload
			);
		}
	};

	const setAnalyticsEvent = (analyticsEvent) => {
		setChat((currentChat) => ({ ...currentChat, analyticsEvent }));
	};

	const openChatModalInvite = () => {
		setAnalyticsEvent("chatInviteAccept");
		removeFocusModalListener();
	};

	const closeChatModalInvite = () => {
		setAnalyticsEvent("chatInviteDecline");
		removeFocusModalListener();
	};

	const triggerChatModalButtonAnalytics = (add = true) => {
		triggerEventListener("inviteClose", closeChatModalInvite, add);
		triggerEventListener("inviteReject", closeChatModalInvite, add);
		triggerEventListener("inviteAccept", openChatModalInvite, add);
	};

	const addChatWindowAnalytics = (event) => {
		const chatState = event?.state || "";
		switch (chatState) {
			case "init":
				setAnalyticsEvent("chatView");
				break;
			case "ended":
				setAnalyticsEvent("chatClose");
				break;
			case "chatting":
				if (!sendedInteractionEvent) {
					sendedInteractionEvent = true;
					setAnalyticsEvent("chatInteract");
				}
				break;
			default:
				break;
		}
	};

	const loadRoomsAvailabilityScript = () => {
		const proactiveWasTriggered =
			window.sessionStorage.getItem("navigationProactiveTriggered") || false;
		if (
			(brands[brand]?.validateRoomsAvailability &&
				!isCurrentPathPresent(
					brands[brand]?.PathsToValidateRoomsAvailability
				)) ||
			proactiveWasTriggered ||
			!brands[brand]?.validateRoomsAvailability
		) {
			return;
		}

		dynamicallyLoadScript(mapString(roomsAvailabilityScript, brand));
	};

	const getConversationId = () => {
		const currentConversationId = chatScript?.events?.hasFired
			? chatScript.events
					.hasFired("conversationInfo")
					?.find((event) => event?.data?.conversationId)?.data.conversationId
			: null;
		if (currentConversationId) {
			setConversationId(currentConversationId);
			return currentConversationId;
		}
		return null;
	};

	const executeChatAuthenticationLogic = useCallback(async () => {
		const section = [];
		let identitiesInterval = null;
		let sdes = chatScript.sdes || [];
		try {
			chatScript?.events.bind({
				eventName: "state",
				appName: "lpUnifiedWindow",
				func: addChatWindowAnalytics,
			});

			if (!chatScript.identities?.length) {
				let iterations = 0;
				await new Promise((resolve, reject) => {
					identitiesInterval = setInterval(() => {
						if (chatScript.identities?.length) {
							resolve();
						} else {
							iterations++;
							if (iterations > maxIdentitiesInterval) {
								reject("LpTag -> Identities were not correctly defined");
							}
						}
					}, 500);
				});
				clearInterval(identitiesInterval);
			}

			const customerInfo = {
				type: "ctmrinfo",
				info: {
					customerId: user?.swid,
					loginStatus: user ? "1" : "0",
				},
				isLoggedIn: user?.swid ? true : false,
			};

			if (chatConfiguration.salesPages) {
				customerInfo.info.ctype = "dvcsales";
			}

			if (user) {
				const decodedPayload = decodeJsonWebToken(user.authenticatedChatToken);
				if (decodedPayload) {
					const identityFn = (callback) => {
						callback({
							iss: decodedPayload.iss,
							acr: "loa1",
							sub: decodedPayload.sub,
						});
					};
					window.lpGetAuthenticationToken = (callback) => {
						window.chatScriptTokenFunctionWasCalled = {
							value: user.authenticatedChatToken,
						};
						callback(user.authenticatedChatToken);
					};
					chatScript.identities = chatScript.identities || [];
					chatScript.identities.push(identityFn);
					sdes.push({
						type: "personal",
						personal: {
							firstname: user.firstName,
						},
					});
					customerInfo.info.userName = user.firstName;
					if (user?.swid) {
						section.push("auth");
						if (user.membership) {
							if (chatConfiguration?.sendMemberDetails) {
								customerInfo.info.accountName = user.memberClubId;
							}
							if (
								customerInfo.info.ctype &&
								!isCurrentPathPresent(chatConfiguration.salesPages)
							) {
								customerInfo.info.ctype = "dvcservice";
							}
						}
					}
				}
			}
			section.push("unauth");
			sdes.push(customerInfo);
			chatScript.newPage(window.location.href, {
				section,
				sdes,
			});
			publishChatEvent(user?.swid ? true : false);
			document.addEventListener(chatEventListener, chatEventLogic);
			loadRoomsAvailabilityScript();
		} catch (error) {
			console.error(error);
			if (identitiesInterval) clearInterval(identitiesInterval);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatScript, user]);

	const loadChatElements = () => {
		setChat({
			chatWindow: document.getElementById(chatWindowId),
			chatModal: document.getElementById(chatInviteModalId),
			chatFloatingButton: document.getElementById(chatFloatingButtonId),
			analyticsEvent: null,
		});
	};

	useEffect(() => {
		if (analyticsEvent) {
			sendEvent(analyticsEvent, {
				chatSessionId: conversationId || getConversationId(),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsEvent]);

	useEffect(() => {
		if (chatScript && !isLoading && !executingAuthenticationLogic) {
			if (user && !user.isDataLoaded) return;
			executingAuthenticationLogic = true;
			chatScript.start && chatScript.start();
			if (
				chatConfiguration?.excludedPages &&
				isCurrentPathPresent(chatConfiguration.excludedPages)
			) {
				return;
			}
			if (chatConfiguration?.authenticatedChat) {
				chatScript.events.bind({
					eventName: "ON_READY",
					appName: "LPTAG",
					func: executeChatAuthenticationLogic(),
					triggerOnce: true,
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatScript, isLoading, user, executeChatAuthenticationLogic]);

	useEffect(() => {
		isMounted = true;
		if (window[chatScriptKey]?.site) {
			setChatScript(window[chatScriptKey]);
		} else {
			document.addEventListener(chatScriptKey, setChatScriptValue);
		}
		return () => {
			isMounted = false;
		};
	}, [setChatScript, setChatScriptValue]);

	useEffect(() => {
		if (chatModal) {
			if (!addedAnalyticsEvents) {
				addedAnalyticsEvents = true;
				const chatModalTitle = document.querySelector(
					`#${chatInviteModalId} .syndicated-chat-invite__header__title`
				);
				if (ariaLiveRef?.current && chatModalTitle) {
					document.addEventListener("keydown", addFocusModalEvent);
					ariaLiveRef.current.textContent = chatModalTitle.textContent;
				}
				setAnalyticsEvent("chatInviteView");
				triggerChatModalButtonAnalytics();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatModal]);

	useEffect(() => {
		if (chatConfiguration?.checkForChatElements) {
			loadChatElements();
			if (!observer) {
				observer = new MutationObserver(() => {
					loadChatElements();
				});
				observer.observe(document.body, {
					childList: true,
					subtree: true,
				});
			}
		}
		return () => {
			removeHandleNodeListener();
			triggerChatModalButtonAnalytics(false);
			document.removeEventListener(chatScriptKey, setChatScriptValue);
			document.removeEventListener(chatEventListener, chatEventLogic);
			removeFocusModalListener();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderChatButtons = () => {
		return (chatConfiguration?.chatButtonIds || ["lpChatButton"]).map(
			(chatButtonId) => {
				if (
					!document.querySelector(`#${chatButtonId}:not(.${chatButtonClass})`)
				) {
					return (
						<div
							key={chatButtonId}
							id={chatButtonId}
							className={chatButtonClass}
							tabIndex="0"
							aria-hidden="true"
							role="button"
						></div>
					);
				} else {
					return null;
				}
			}
		);
	};

	return renderChatButtons();
};

export default Chat;
